'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import PublicNav from '@/components/PublicNav';
import HomeFooter from '@/app/components/HomeFooter';
import { CheckCircle, Filter, X, Lock, Search } from 'lucide-react';

interface Candidate {
  id: string;
  name: string;
  province: string;
  jobType: string;
  skills: string[];
  languages: string[];
  availability: string;
  drivingLicence: boolean;
  verified: boolean;
  score: number;
  workTypes: string[];
}

const candidates: Candidate[] = [
  { id: '1', name: 'Thandi', province: 'Gauteng', jobType: 'Administration', skills: ['Admin', 'Data Entry', 'MS Office'], languages: ['English', 'Zulu'], availability: 'Immediately', drivingLicence: false, verified: true, score: 91, workTypes: ['Full-time', 'Contract'] },
  { id: '2', name: 'Sipho', province: 'Western Cape', jobType: 'Logistics', skills: ['Logistics', 'Driving', 'Warehousing'], languages: ['English', 'Afrikaans'], availability: 'Immediately', drivingLicence: true, verified: true, score: 88, workTypes: ['Full-time'] },
  { id: '3', name: 'Nomsa', province: 'KwaZulu-Natal', jobType: 'Education', skills: ['Childcare', 'Teaching', 'Tutoring'], languages: ['English', 'Zulu', 'Xhosa'], availability: '2 weeks', drivingLicence: false, verified: false, score: 72, workTypes: ['Part-time', 'Full-time'] },
  { id: '4', name: 'Kagiso', province: 'Gauteng', jobType: 'IT & Technology', skills: ['IT Support', 'Networking', 'Helpdesk'], languages: ['English', 'Sotho'], availability: 'Immediately', drivingLicence: true, verified: true, score: 94, workTypes: ['Full-time', 'Contract'] },
  { id: '5', name: 'Lerato', province: 'Free State', jobType: 'Retail & Sales', skills: ['Retail', 'Sales', 'Customer Service'], languages: ['English', 'Sotho', 'Afrikaans'], availability: 'Immediately', drivingLicence: false, verified: true, score: 85, workTypes: ['Full-time', 'Part-time'] },
  { id: '6', name: 'Bongani', province: 'Eastern Cape', jobType: 'Construction', skills: ['Construction', 'Labour', 'Tiling'], languages: ['English', 'Xhosa'], availability: '1 week', drivingLicence: false, verified: true, score: 79, workTypes: ['Full-time', 'Contract'] },
  { id: '7', name: 'Ayanda', province: 'Gauteng', jobType: 'Finance', skills: ['Accounting', 'Bookkeeping', 'Payroll'], languages: ['English', 'Zulu'], availability: '1 month', drivingLicence: true, verified: true, score: 96, workTypes: ['Full-time'] },
  { id: '8', name: 'Zanele', province: 'Limpopo', jobType: 'Healthcare', skills: ['Nursing', 'Patient Care', 'First Aid'], languages: ['English', 'Sotho', 'Venda'], availability: '2 weeks', drivingLicence: false, verified: false, score: 68, workTypes: ['Full-time', 'Shift work'] },
  { id: '9', name: 'Mpho', province: 'North West', jobType: 'Hospitality', skills: ['Catering', 'Waitering', 'Events'], languages: ['English', 'Tswana'], availability: 'Immediately', drivingLicence: false, verified: true, score: 83, workTypes: ['Full-time', 'Part-time'] },
  { id: '10', name: 'Tebogo', province: 'Mpumalanga', jobType: 'Security', skills: ['Security', 'Safety', 'PSIRA Registered'], languages: ['English', 'Zulu', 'Sotho'], availability: 'Immediately', drivingLicence: true, verified: true, score: 77, workTypes: ['Full-time', 'Shift work'] },
  { id: '11', name: 'Nandi', province: 'Western Cape', jobType: 'Marketing', skills: ['Marketing', 'Social Media', 'Content Creation'], languages: ['English', 'Afrikaans'], availability: '2 weeks', drivingLicence: false, verified: true, score: 89, workTypes: ['Full-time', 'Freelance'] },
  { id: '12', name: 'Lebo', province: 'Gauteng', jobType: 'Customer Service', skills: ['Customer Service', 'Call Centre', 'CRM'], languages: ['English', 'Sotho', 'Zulu'], availability: 'Immediately', drivingLicence: false, verified: true, score: 82, workTypes: ['Full-time', 'Part-time'] },
];

const provinces = ['Gauteng', 'Western Cape', 'KwaZulu-Natal', 'Eastern Cape', 'Free State', 'Limpopo', 'Mpumalanga', 'North West', 'Northern Cape'];
const jobTypes = ['Administration', 'Logistics', 'Education', 'IT & Technology', 'Retail & Sales', 'Construction', 'Finance', 'Healthcare', 'Hospitality', 'Security', 'Marketing', 'Customer Service'];

const scoreColor = (score: number) => {
  if (score >= 90) return 'var(--charcoal)';
  if (score >= 75) return 'var(--amber)';
  return 'var(--charcoal)';
};

export default function DirectoryPage() {
  const [selectedProvinces, setSelectedProvinces] = useState<string[]>([]);
  const [selectedJobTypes, setSelectedJobTypes] = useState<string[]>([]);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);

  const toggleFilter = (arr: string[], val: string, setter: (v: string[]) => void) => {
    setter(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);
  };

  const filtered = candidates.filter((c) => {
    if (selectedProvinces.length > 0 && !selectedProvinces.includes(c.province)) return false;
    if (selectedJobTypes.length > 0 && !selectedJobTypes.includes(c.jobType)) return false;
    if (verifiedOnly && !c.verified) return false;
    if (searchQuery && !c.name.toLowerCase().includes(searchQuery.toLowerCase()) && !c.skills.some(s => s.toLowerCase().includes(searchQuery.toLowerCase()))) return false;
    return true;
  });

  const handleUnlock = (candidate: Candidate) => {
    setSelectedCandidate(candidate);
    setShowUnlockModal(true);
  };

  return (
    <div className="min-h-screen bg-white">
      <PublicNav />

      {/* Header */}
      <section className="py-10 lg:py-14" style={{ background: 'var(--amber)' }}>
        <div className="max-w-screen-xl mx-auto px-4 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--charcoal)', opacity: 0.6 }}>Screened Talent Directory</p>
          <h1 className="text-3xl lg:text-4xl font-bold mb-2" style={{ color: 'var(--charcoal)', fontFamily: 'var(--font-serif)' }}>
            Browse Verified Candidates
          </h1>
          <p className="max-w-xl mb-6" style={{ color: 'var(--charcoal)', opacity: 0.7 }}>
            All candidates have completed our Work-Readiness Assessment, ID verification and reference check.
          </p>
          <div className="relative max-w-md">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
            <input
              type="text"
              placeholder="Search by name or skill..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-xl border text-sm outline-none"
              style={{ background: 'white', borderColor: 'rgba(43,41,38,0.2)', color: 'var(--charcoal)' }}
            />
          </div>
        </div>
      </section>

      <div className="max-w-screen-xl mx-auto px-4 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Filter sidebar */}
          <aside className="hidden lg:block w-56 flex-shrink-0">
            <div className="sticky top-24">
              <div className="font-bold text-sm mb-4 flex items-center gap-2" style={{ color: 'var(--charcoal)' }}>
                <Filter size={15} />
                Filter Candidates
              </div>

              <div className="mb-5">
                <label className="flex items-center gap-2 cursor-pointer mb-4">
                  <input
                    type="checkbox"
                    checked={verifiedOnly}
                    onChange={(e) => setVerifiedOnly(e.target.checked)}
                    style={{ accentColor: 'var(--charcoal)' }}
                  />
                  <span className="text-sm font-medium" style={{ color: 'var(--charcoal)' }}>Verified Only</span>
                </label>
              </div>

              <div className="mb-6">
                <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">Province</div>
                <div className="flex flex-col gap-2">
                  {provinces.slice(0, 6).map((p) => (
                    <label key={p} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedProvinces.includes(p)}
                        onChange={() => toggleFilter(selectedProvinces, p, setSelectedProvinces)}
                        style={{ accentColor: 'var(--charcoal)' }}
                      />
                      <span className="text-sm text-muted-foreground">{p}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">Job Type</div>
                <div className="flex flex-col gap-2">
                  {jobTypes.slice(0, 6).map((jt) => (
                    <label key={jt} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedJobTypes.includes(jt)}
                        onChange={() => toggleFilter(selectedJobTypes, jt, setSelectedJobTypes)}
                        style={{ accentColor: 'var(--charcoal)' }}
                      />
                      <span className="text-sm text-muted-foreground">{jt}</span>
                    </label>
                  ))}
                </div>
              </div>

              {(selectedProvinces.length > 0 || selectedJobTypes.length > 0 || verifiedOnly) && (
                <button
                  onClick={() => { setSelectedProvinces([]); setSelectedJobTypes([]); setVerifiedOnly(false); }}
                  className="text-xs text-muted-foreground hover:text-foreground underline"
                >
                  Clear all filters
                </button>
              )}
            </div>
          </aside>

          {/* Candidate grid */}
          <div className="flex-1">
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-muted-foreground">{filtered.length} candidates found</p>
              <button
                className="lg:hidden flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-xl border"
                style={{ borderColor: 'var(--border)', color: 'var(--foreground)' }}
                onClick={() => setFilterOpen(!filterOpen)}
              >
                <Filter size={14} />
                Filter
              </button>
            </div>

            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {filtered.map((candidate) => (
                <div key={candidate.id} className="rounded-2xl border bg-white flex flex-col overflow-hidden hover:shadow-md transition-shadow duration-200" style={{ borderColor: 'var(--border)' }}>
                  <div className="p-5 flex items-start gap-4">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold flex-shrink-0"
                      style={{ background: 'var(--amber)', color: 'var(--charcoal)' }}
                    >
                      {candidate.name.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <div className="font-bold text-base" style={{ color: 'var(--charcoal)' }}>{candidate.name}</div>
                        {candidate.verified && (
                          <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--vetted-bg)', color: 'var(--vetted-color)' }}>
                            <CheckCircle size={9} />
                            Verified
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">{candidate.province} · {candidate.jobType}</div>
                    </div>
                  </div>

                  <div className="px-5 pb-4 flex-1 flex flex-col gap-3">
                    <div className="flex flex-wrap gap-1.5">
                      {candidate.skills?.map((skill) => (
                        <span key={skill} className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}>
                          {skill}
                        </span>
                      ))}
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="text-xs text-muted-foreground">
                        Available: <span className="font-medium" style={{ color: 'var(--charcoal)' }}>{candidate.availability}</span>
                      </div>
                      {candidate.drivingLicence && (
                        <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--off-white)', color: 'var(--charcoal)' }}>
                          🚗 Licence
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="text-xs font-medium text-muted-foreground">Work-Readiness</div>
                      <div className="flex-1 h-1.5 rounded-full" style={{ background: 'var(--muted)' }}>
                        <div className="h-1.5 rounded-full" style={{ background: scoreColor(candidate.score), width: `${candidate.score}%` }} />
                      </div>
                      <div className="text-xs font-bold" style={{ color: scoreColor(candidate.score) }}>{candidate.score}</div>
                    </div>
                  </div>

                  <div className="px-5 pb-5 flex gap-2">
                    <Link
                      href={`/candidate-profile?id=${candidate.id}`}
                      className="flex-1 inline-flex items-center justify-center px-3 py-2 rounded-xl text-xs font-bold border-2 transition-all duration-150"
                      style={{ borderColor: 'var(--charcoal)', color: 'var(--charcoal)' }}
                    >
                      View Profile
                    </Link>
                    <button
                      onClick={() => handleUnlock(candidate)}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold transition-all duration-150 active:scale-95"
                      style={{ background: 'var(--charcoal)', color: 'var(--amber)' }}
                    >
                      <Lock size={11} />
                      Unlock — R1,000
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Unlock Modal */}
      {showUnlockModal && selectedCandidate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold mb-1" style={{ color: 'var(--charcoal)', fontFamily: 'var(--font-serif)' }}>Unlock Full Profile</h2>
                <p className="text-sm text-muted-foreground">{selectedCandidate.name} · {selectedCandidate.province} · {selectedCandidate.jobType}</p>
              </div>
              <button onClick={() => setShowUnlockModal(false)} className="p-1 rounded-lg hover:bg-muted transition-colors">
                <X size={20} />
              </button>
            </div>

            <div className="rounded-xl p-5 mb-6" style={{ background: 'var(--off-white)' }}>
              <div className="text-2xl font-bold mb-1" style={{ color: 'var(--charcoal)', fontFamily: 'var(--font-serif)' }}>R1,000</div>
              <div className="text-xs text-muted-foreground mb-4">One-time payment · Instant access</div>
              <div className="flex flex-col gap-2">
                {['Full CV access', 'Contact details (phone & email)', 'Verification information', 'Interview notes & assessment summary', 'Work-Readiness Score breakdown']?.map((item) => (
                  <div key={item} className="flex items-center gap-2 text-sm">
                    <CheckCircle size={13} style={{ color: 'var(--charcoal)' }} />
                    {item}
                  </div>
                ))}
              </div>
            </div>

            <div className="text-xs text-muted-foreground mb-4">Payment via: PayFast · Peach Payments · Ozow</div>

            <button
              className="w-full py-3 rounded-xl font-bold text-white transition-all duration-150 active:scale-95"
              style={{ background: 'var(--primary)' }}
            >
              Unlock Profile — R1,000
            </button>
            <button
              onClick={() => setShowUnlockModal(false)}
              className="w-full mt-2 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <HomeFooter />
    </div>
  );
}
